import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Laptop, Lock, Printer } from 'lucide-react';
import { DateTime } from 'luxon';
import { ATTENDANCE_STATUS_LABELS, type AttendanceStatus, type DailyReportDto, type ProductivityDayDto, type ProductivitySummaryDto } from '@workforce/shared';
import { formatMinutes, t } from '@/lib/i18n';
import { api, qs } from '@/lib/api';
import { useTz } from '@/lib/timezone';
import { useCurrentPeriod } from '@/lib/periods';
import { Badge, Button, Card, EmptyState, ErrorBanner, Field, Input, Select, Spinner } from '@/components/ui';
import { CLASS_COLORS, pctTone } from '@/components/ProductivityParts';
import { RANGE_OPTIONS, rangeFor, type RangePreset } from '@/components/AttendanceTable';
import { activeHourRange, ChartLegend, HOUR_CELL, HourStrip, ScoreBars, StackedColumns, StatTile, type ClassKey } from '@/components/Charts';
import { ExportMenu } from '@/components/ExportMenu';

const valuesOf = (d: { productiveMinutes: number; neutralMinutes: number; unknownMinutes: number; unproductiveMinutes: number; idleMinutes: number }): Record<ClassKey, number> => ({
  productive: d.productiveMinutes,
  neutral: d.neutralMinutes,
  unknown: d.unknownMinutes,
  unproductive: d.unproductiveMinutes,
  idle: d.idleMinutes,
});

/** Range picker shared by the dashboard (presets incl. the reporting period, or custom dates). */
function useRange(zone: string) {
  const periods = useCurrentPeriod();
  const [preset, setPreset] = useState<RangePreset>('period');
  const [touched, setTouched] = useState(false);
  const [custom, setCustom] = useState(() => rangeFor('week', zone));
  // In the first days of a new period there is little to show: start on the last one.
  useEffect(() => {
    if (touched || !periods.data?.previous) return;
    const started = DateTime.fromISO(periods.data.current.startDate);
    if (DateTime.now().setZone(zone).startOf('day').diff(started, 'days').days < 3) setPreset('last_period');
  }, [periods.data, touched, zone]);
  const range = rangeFor(preset, zone, custom, periods.data);
  const controls = (
    <div className="flex flex-wrap items-end gap-3">
      <Field label={t('Period')}>
        <Select value={preset} onChange={(e) => { setTouched(true); setPreset(e.target.value as RangePreset); }} aria-label={t('Period')}>
          {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
        </Select>
      </Field>
      {preset === 'custom' && (
        <>
          <Field label={t('From')}><Input type="date" value={custom.from} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, from: e.target.value }))} /></Field>
          <Field label={t('To')}><Input type="date" value={custom.to} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, to: e.target.value }))} /></Field>
        </>
      )}
    </div>
  );
  return { range, controls, ready: preset !== 'period' && preset !== 'last_period' ? true : !!periods.data || periods.isError };
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
export function DashboardTab({ onEmployee, onDay }: { onEmployee: (id: string) => void; onDay: (date: string) => void }) {
  const { companyZone } = useTz();
  const { range, controls, ready } = useRange(companyZone);
  const q = useQuery({
    queryKey: ['productivity', 'summary', range.from, range.to],
    queryFn: () => api.get<ProductivitySummaryDto>(`/productivity/summary${qs(range)}`),
    enabled: ready,
  });
  const s = q.data;
  const days = s?.daily ?? [];
  const busy = s?.employees.filter((e) => e.totals.trackedMinutes > 0) ?? [];
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {controls}
        <div className="flex items-center gap-3">
          <p className="text-xs text-slate-500">
            {DateTime.fromISO(range.from).toFormat('d LLL')} – {DateTime.fromISO(range.to).toFormat('d LLL yyyy')}
          </p>
          <ExportMenu kind="productivity" params={{ from: range.from, to: range.to }} />
        </div>
      </div>
      <ErrorBanner error={q.error} />
      {q.isLoading || !s ? (
        <Spinner />
      ) : !s.employees.length ? (
        <Card><EmptyState icon={<Laptop className="h-6 w-6" />} title={t('No computers connected yet')} description={t("Install the desktop agent on each engineer's laptop (Computers tab).")} /></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-6">
            <StatTile hero label={t('Team score')} value={<span className={pctTone(s.totals.productivityPct, s.targetPct)}>{s.totals.productivityPct == null ? '—' : `${s.totals.productivityPct}%`}</span>} note={t('Target {pct}%', { pct: s.targetPct })} />
            <StatTile label={t('Tracked')} value={formatMinutes(s.totals.trackedMinutes)} note={t('{n} of {total} people', { n: busy.length, total: s.employees.length })} />
            <StatTile label={t('Productive')} value={formatMinutes(s.totals.productiveMinutes)} />
            <StatTile label={t('Unproductive')} value={formatMinutes(s.totals.unproductiveMinutes)} />
            <StatTile label={t('Not classified')} value={formatMinutes(s.totals.unknownMinutes)} note={s.totals.unknownMinutes ? t('Classify in “Not classified”') : undefined} />
            <StatTile label={t('Idle')} value={formatMinutes(s.totals.idleMinutes)} />
          </div>

          <div className="grid gap-5 lg:grid-cols-3">
            <Card className="lg:col-span-2" title={t('Hours per day')} actions={<ChartLegend />}>
              <StackedColumns
                ariaLabel={t('Team hours per day by class')}
                onSelect={onDay}
                data={days.map((d) => ({
                  key: d.date,
                  label: days.length > 16 ? DateTime.fromISO(d.date).toFormat('d') : DateTime.fromISO(d.date).toFormat('ccc d'),
                  title: DateTime.fromISO(d.date).toFormat('cccc d LLL'),
                  values: valuesOf(d),
                  extra: (
                    <p className="mt-1 text-slate-500">
                      {d.trackedMinutes ? (d.days === 1 ? t('Score {pct}% · {n} person · click for the daily report', { pct: d.productivityPct, n: d.days }) : t('Score {pct}% · {n} people · click for the daily report', { pct: d.productivityPct, n: d.days })) : t('No activity recorded')}
                    </p>
                  ),
                }))}
              />
            </Card>
            <Card title={t('Score by person')}>
              {busy.length ? (
                <ScoreBars
                  target={s.targetPct}
                  onSelect={onEmployee}
                  rows={[...busy]
                    .sort((a, b) => (b.totals.productivityPct ?? -1) - (a.totals.productivityPct ?? -1))
                    .map((e) => ({ key: e.employeeId, label: e.employeeName, pct: e.totals.productivityPct, detail: t('{time} tracked over {n} day(s)', { time: formatMinutes(e.totals.trackedMinutes), n: e.totals.days }) }))}
                />
              ) : (
                <p className="text-sm text-slate-500">{t('Nothing recorded in this period yet.')}</p>
              )}
            </Card>
          </div>

          <div className="grid gap-5 lg:grid-cols-3">
            <Card className="lg:col-span-2" title={t('Time of day')} actions={<ChartLegend />}>
              <StackedColumns
                ariaLabel={t('Team activity by hour of day')}
                height={250}
                data={s.hours
                  .filter((h, i, all) => {
                    const r = activeHourRange([all]);
                    return i >= r.from && i <= r.to;
                  })
                  .map((h) => ({
                    key: String(h.hour),
                    label: String(h.hour).padStart(2, '0'),
                    title: t('{from} – {to} (all days, everyone)', { from: `${String(h.hour).padStart(2, '0')}:00`, to: `${String((h.hour + 1) % 24).padStart(2, '0')}:00` }),
                    values: { productive: h.productive, neutral: h.neutral, unknown: h.unknown, unproductive: h.unproductive, idle: h.idle },
                  }))}
              />
            </Card>
            <Card title={t('Most used apps & websites')} bodyClass="p-0">
              <TopApps apps={s.topApps} />
            </Card>
          </div>

          <Card title={t('People')} bodyClass="p-0">
            <PeopleTable s={s} onEmployee={onEmployee} />
          </Card>
        </>
      )}
    </div>
  );
}

function TopApps({ apps }: { apps: ProductivitySummaryDto['topApps'] }) {
  if (!apps.length) return <p className="px-5 py-4 text-sm text-slate-500">{t('Nothing yet.')}</p>;
  const max = Math.max(...apps.map((a) => a.minutes), 1);
  return (
    <ul className="divide-y divide-slate-100">
      {apps.slice(0, 7).map((a) => (
        <li key={`${a.kind}:${a.key}`} className="px-5 py-1.5 text-sm" title={a.kind === 'domain' ? (a.employees === 1 ? t('Website · used by {n} person', { n: a.employees }) : t('Website · used by {n} people', { n: a.employees })) : a.employees === 1 ? t('App · used by {n} person', { n: a.employees }) : t('App · used by {n} people', { n: a.employees })}>
          <div className="flex items-center gap-2">
            <span className={clsx('h-2 w-2 shrink-0 rounded-full', CLASS_COLORS[a.classification])} />
            <span className="min-w-0 flex-1 truncate" title={a.key}>{a.label}</span>
            <span className="tabular-nums text-slate-600">{formatMinutes(a.minutes)}</span>
          </div>
          <div className="ms-4 mt-1 h-1 rounded-full bg-slate-100">
            <div className={clsx('h-1 rounded-full', CLASS_COLORS[a.classification])} style={{ width: `${(a.minutes / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function PeopleTable({ s, onEmployee }: { s: ProductivitySummaryDto; onEmployee: (id: string) => void }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-start text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2">{t('Employee')}</th>
            <th className="hidden px-3 py-2 text-end sm:table-cell">{t('Days')}</th>
            <th className="px-3 py-2 text-end">{t('Tracked')}</th>
            <th className="hidden px-3 py-2 text-end md:table-cell">{t('Productive')}</th>
            <th className="hidden px-3 py-2 text-end md:table-cell">{t('Unproductive')}</th>
            <th className="hidden px-3 py-2 text-end lg:table-cell">{t('Not classified')}</th>
            <th className="hidden px-3 py-2 text-end lg:table-cell">{t('Idle')}</th>
            <th className="px-3 py-2 text-end">{t('Score')}</th>
          </tr>
        </thead>
        <tbody>
          {s.employees.map((e) => (
            <tr key={e.employeeId} className="border-b border-slate-100">
              <td className="px-4 py-2">
                <button className="font-medium text-brand-700 hover:underline" onClick={() => onEmployee(e.employeeId)}>{e.employeeName}</button>
                {e.departmentName && <p className="text-xs text-slate-400">{e.departmentName}</p>}
              </td>
              <td className="hidden px-3 py-2 text-end tabular-nums sm:table-cell">{e.totals.days}</td>
              <td className="px-3 py-2 text-end tabular-nums">{formatMinutes(e.totals.trackedMinutes)}</td>
              <td className="hidden px-3 py-2 text-end tabular-nums md:table-cell">{formatMinutes(e.totals.productiveMinutes)}</td>
              <td className="hidden px-3 py-2 text-end tabular-nums md:table-cell">{formatMinutes(e.totals.unproductiveMinutes)}</td>
              <td className="hidden px-3 py-2 text-end tabular-nums lg:table-cell">{formatMinutes(e.totals.unknownMinutes)}</td>
              <td className="hidden px-3 py-2 text-end tabular-nums lg:table-cell">{formatMinutes(e.totals.idleMinutes)}</td>
              <td className={clsx('px-3 py-2 text-end font-semibold tabular-nums', pctTone(e.totals.productivityPct, s.targetPct))}>
                {e.totals.productivityPct == null ? '—' : `${e.totals.productivityPct}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Daily report
// ---------------------------------------------------------------------------
const hm = (iso: string | null, zone: string) => (iso ? DateTime.fromISO(iso).setZone(zone).toFormat('HH:mm') : '—');

export function DailyReportTab({ onEmployee }: { onEmployee: (id: string) => void }) {
  const { companyZone } = useTz();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? DateTime.now().setZone(companyZone).toISODate()!;
  const setDate = (d: string) => setParams((p) => { p.set('date', d); return p; }, { replace: true });
  const q = useQuery({
    queryKey: ['productivity', 'daily-report', date],
    queryFn: () => api.get<DailyReportDto>(`/productivity/daily-report?date=${date}`),
    refetchInterval: 120_000,
  });
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const r = q.data;
  const range = activeHourRange((r?.rows ?? []).flatMap((x) => (x.productivity ? [x.productivity.hours] : [])));
  const shift = (n: number) => setDate(DateTime.fromISO(date).plus({ days: n }).toISODate()!);
  const tracked = r?.rows.filter((x) => x.productivity && x.productivity.trackedMinutes > 0) ?? [];
  const frozen = r?.rows.some((x) => x.productivity?.frozen);
  const firstHour = r?.rows.find((x) => x.productivity)?.productivity?.hours[range.from]?.hour ?? 0;
  const lastHour = r?.rows.find((x) => x.productivity)?.productivity?.hours[range.to]?.hour ?? 23;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3 print:hidden">
        <Field label={t('Date')}>
          <div className="flex gap-1">
            <Button variant="secondary" onClick={() => shift(-1)} aria-label={t('Previous day')}>‹</Button>
            <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
            <Button variant="secondary" onClick={() => shift(1)} aria-label={t('Next day')}>›</Button>
          </div>
        </Field>
        <div className="flex items-center gap-4">
          <ChartLegend />
          <ExportMenu kind="productivity-daily" params={{ date }} />
          <Button variant="secondary" icon={<Printer className="h-4 w-4" />} onClick={() => window.print()}>{t('Print')}</Button>
        </div>
      </div>
      <h2 className="hidden text-lg font-semibold print:block">{t('Daily productivity report — {date}', { date: DateTime.fromISO(date).toFormat('cccc d LLLL yyyy') })}</h2>
      <ErrorBanner error={q.error} />
      {q.isLoading || !r ? (
        <Spinner />
      ) : !r.rows.length ? (
        <Card><EmptyState title={t('Nobody to report on')} description={t('People appear here when they clock in or have a computer connected.')} /></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-5">
            <StatTile label={t('Team score')} value={<span className={pctTone(r.totals.productivityPct, r.targetPct)}>{r.totals.productivityPct == null ? '—' : `${r.totals.productivityPct}%`}</span>} note={t('Target {pct}%', { pct: r.targetPct })} />
            <StatTile label={t('People with activity')} value={tracked.length} note={t('of {n} listed', { n: r.rows.length })} />
            <StatTile label={t('Productive')} value={formatMinutes(r.totals.productiveMinutes)} />
            <StatTile label={t('Unproductive')} value={formatMinutes(r.totals.unproductiveMinutes)} />
            <StatTile label={t('Idle')} value={formatMinutes(r.totals.idleMinutes)} />
          </div>
          {frozen && (
            <p className="flex items-center gap-1.5 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" /> {t('This day is in a closed reporting period — figures are frozen.')}</p>
          )}
          <Card bodyClass="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-start text-xs font-medium uppercase tracking-wide text-slate-500">
                    <th className="w-8 px-2 py-2 print:hidden" />
                    <th className="px-3 py-2">{t('Employee')}</th>
                    <th className="whitespace-nowrap px-3 py-2">{t('Clock in – out')}</th>
                    <th className="hidden px-3 py-2 text-end sm:table-cell">{t('Worked')}</th>
                    <th className="hidden px-3 py-2 md:table-cell">
                      <span className="block whitespace-nowrap">{t('Computer activity')}</span>
                      <span className="flex justify-between font-normal normal-case tracking-normal text-slate-400" style={{ width: (range.to - range.from + 1) * HOUR_CELL }}>
                        <span>{String(firstHour).padStart(2, '0')}:00</span>
                        <span>{String((lastHour + 1) % 24).padStart(2, '0')}:00</span>
                      </span>
                    </th>
                    <th className="px-3 py-2 text-end">{t('Productive')}</th>
                    <th className="hidden px-3 py-2 text-end lg:table-cell">{t('Unproductive')}</th>
                    <th className="hidden px-3 py-2 text-end lg:table-cell">{t('Idle')}</th>
                    <th className="px-3 py-2 text-end">{t('Score')}</th>
                  </tr>
                </thead>
                <tbody>
                  {r.rows.map((row) => {
                    const p = row.productivity;
                    const a = row.attendance;
                    const canOpen = !!p && p.trackedMinutes > 0;
                    const zone = p?.timezone ?? companyZone;
                    const statusShown = a && !['present', 'in_progress', 'future'].includes(a.status);
                    return (
                      <Fragment key={row.employeeId}>
                        <tr className={clsx('border-b border-slate-100', canOpen && 'cursor-pointer hover:bg-slate-50')} onClick={() => canOpen && setOpen((o) => ({ ...o, [row.employeeId]: !o[row.employeeId] }))}>
                          <td className="px-2 py-2 text-slate-400 print:hidden">{canOpen && (open[row.employeeId] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}</td>
                          <td className="whitespace-nowrap px-3 py-2">
                            <button className="font-medium text-brand-700 hover:underline" onClick={(e) => { e.stopPropagation(); onEmployee(row.employeeId); }}>{row.employeeName}</button>
                            {row.departmentName && <p className="text-xs text-slate-400">{row.departmentName}</p>}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                            {a?.firstIn ? `${hm(a.firstIn, zone)} – ${a.lastOut ? hm(a.lastOut, zone) : '…'}` : <span className="text-slate-400">—</span>}
                            {a?.hoursSource === 'computer' && <p className="text-[11px] text-slate-400">{t('From computer — no clocking in')}</p>}
                            {statusShown && (
                              <div className="mt-0.5">
                                <Badge tone={a!.status === 'absent' ? 'red' : a!.status === 'late' || a!.status === 'incomplete' ? 'amber' : 'slate'}>
                                  {a!.status === 'holiday' && a!.holidayName ? a!.holidayName : t(ATTENDANCE_STATUS_LABELS[a!.status as AttendanceStatus] ?? a!.status)}
                                </Badge>
                              </div>
                            )}
                            {a && a.lateMinutes > 0 && a.status !== 'late' && <p className="text-xs text-amber-700">{t('Late {duration}', { duration: formatMinutes(a.lateMinutes) })}</p>}
                          </td>
                          <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums sm:table-cell">{a && a.workedMinutes ? formatMinutes(a.workedMinutes) : '—'}</td>
                          <td className="hidden px-3 py-2 md:table-cell">
                            {p ? <HourStrip hours={p.hours} from={range.from} to={range.to} /> : <span className="text-xs text-slate-400">{t('No computer connected')}</span>}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-end tabular-nums">{p?.trackedMinutes ? formatMinutes(p.productiveMinutes) : '—'}</td>
                          <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums lg:table-cell">{p?.trackedMinutes ? formatMinutes(p.unproductiveMinutes) : '—'}</td>
                          <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums lg:table-cell">{p?.trackedMinutes ? formatMinutes(p.idleMinutes) : '—'}</td>
                          <td className={clsx('whitespace-nowrap px-3 py-2 text-end font-semibold tabular-nums', pctTone(p?.productivityPct ?? null, r.targetPct))}>
                            {p?.productivityPct == null ? '—' : `${p.productivityPct}%`}
                          </td>
                        </tr>
                        {open[row.employeeId] && p && <AppsRow d={p} />}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
          <p className="text-xs text-slate-500">
            {t('Clock in – out comes from attendance (fingerprint, door QR, app or agent). Computer activity is what the desktop agent recorded while recording was allowed. Score = productive ÷ tracked time (including idle).')}
          </p>
        </>
      )}
    </div>
  );
}

function AppsRow({ d }: { d: ProductivityDayDto }) {
  return (
    <tr className="border-b border-slate-100 bg-slate-50/60">
      <td className="print:hidden" />
      <td colSpan={8} className="px-3 py-3">
        <p className="mb-2 text-xs text-slate-500">
          {t('First activity {first} · last {last} · neutral {neutral} · not classified {unknown}', { first: hm(d.firstActivity, d.timezone), last: hm(d.lastActivity, d.timezone), neutral: formatMinutes(d.neutralMinutes), unknown: formatMinutes(d.unknownMinutes) })}
        </p>
        <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
          {d.apps.map((a) => (
            <div key={`${a.kind}:${a.key}`} className="flex items-center gap-2 text-sm">
              <span className={clsx('h-2 w-2 shrink-0 rounded-full', CLASS_COLORS[a.classification])} />
              <span className="min-w-0 flex-1 truncate" title={a.key}>{a.label}</span>
              <span className="tabular-nums text-slate-600">{formatMinutes(a.minutes)}</span>
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}

/** One person's days as stacked columns (By employee tab and My productivity). */
export function PersonDaysChart({ days }: { days: ProductivityDayDto[] }) {
  if (!days.some((d) => d.trackedMinutes > 0)) return null;
  return (
    <Card title={t('Hours per day')} actions={<ChartLegend />}>
      <StackedColumns
        ariaLabel={t('Hours per day by class')}
        height={200}
        data={days.map((d) => ({
          key: d.date,
          label: days.length > 16 ? DateTime.fromISO(d.date).toFormat('d') : DateTime.fromISO(d.date).toFormat('ccc d'),
          title: DateTime.fromISO(d.date).toFormat('cccc d LLL'),
          values: valuesOf(d),
          extra: d.productivityPct != null ? <p className="mt-1 text-slate-500">{d.frozen ? t('Score {pct}% · frozen', { pct: d.productivityPct }) : t('Score {pct}%', { pct: d.productivityPct })}</p> : undefined,
        }))}
      />
    </Card>
  );
}
